import React, { useState, useEffect, useReducer, useRef, useContext, useMemo, useCallback } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Avatar,
  CircularProgress,
  IconButton,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
  Modal,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  KeyboardArrowDown,
  Cancel,
  Description as DescriptionIcon,
  Reply as ReplyIcon,
  CloudDownload as CloudDownloadIcon,
  HighlightOff as HighlightOffIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Assignment as WordIcon,
  Slideshow as PowerPointIcon,
  TextFields as TextIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Mic as MicIcon,
} from "@material-ui/icons";

import BackArrowIcon from "../../assets/icon/BackArrow.js"

import MarkdownWrapper from "../MarkdownWrapper";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ContactModal from "../ContactModal";
import CustomAudioPlayer from "./CustomAudioPlayer";

import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { REACT_APP_BACKEND_URL } from "../../config";
import { cleanupComponent, getSocket, onSocket } from "../../services/socket";
import { unstable_batchedUpdates } from 'react-dom';
// TicketsContext removido - ahora usando implementación directa
import { AuthContext } from "../../context/Auth/AuthContext";
import { StaticResourcesContext } from "../../context/StaticResources/StaticResourcesContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import {
  isCloudMessageEditDeleteDisabled,
  resolveTicketChannel,
} from "../../utils/serviceWindow";
import {
  formatScheduleTooltip,
  getMessageDisplayTimestamp,
  isScheduledPendingMessage,
  isTemplateMessage,
  parseScheduleMessageMeta,
} from "../../utils/scheduleMessage";
import { translateBackendError } from "../../utils/backendErrorMessage";
import {
  isPaymentMethodRequiredError,
  openCloudPaymentRequiredDialog,
} from "../../utils/openCloudPaymentRequiredDialog";

  // Función auxiliar para formatear fechas de manera segura
const safeFormatDate = (dateString, formatString) => {
  if (!dateString) return "";
  try {
    return format(parseISO(dateString), formatString);
  } catch (error) {
    console.warn("Error al formatear fecha:", dateString, error);
    return "";
  }
};

const FILE_NAME_REGEX = /^[^<>:"/\\|?*\n\r]+\.[a-z0-9]{1,10}$/i;

const getParsedDataJson = (dataJson) => {
  if (!dataJson) return null;

  if (typeof dataJson === "object") {
    return dataJson;
  }

  try {
    return JSON.parse(dataJson);
  } catch {
    return null;
  }
};

const getCloudMediaFromDataJson = (message) => {
  const parsedDataJson = getParsedDataJson(message?.dataJson);
  if (parsedDataJson?.event === 'message.incoming' && parsedDataJson?.media) {
    return parsedDataJson.media;
  }
  return null;
};

const getMediaPayloadFromDataJson = (message) => {
  const parsedDataJson = getParsedDataJson(message?.dataJson);
  const whatsappMessage = parsedDataJson?.message;

  if (!whatsappMessage) return null;

  const mediaPayloads = [
    whatsappMessage.documentMessage,
    whatsappMessage.documentWithCaptionMessage?.message?.documentMessage,
    whatsappMessage.imageMessage,
    whatsappMessage.videoMessage,
    whatsappMessage.audioMessage,
    whatsappMessage.stickerMessage,
  ];

  return mediaPayloads.find(Boolean) || null;
};

const normalizeFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  return fileName.trim();
};

const getFileNameFromContentDisposition = (mediaUrl) => {
  if (!mediaUrl) return "";

  try {
    const url = new URL(mediaUrl);
    const contentDisposition = url.searchParams.get("rscd");
    const fileNameMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] || fileNameMatch?.[2];

    return normalizeFileName(fileName ? decodeURIComponent(fileName) : "");
  } catch {
    return "";
  }
};

const getFileNameFromUrl = (mediaUrl) => {
  if (!mediaUrl) return "";

  const urlParts = mediaUrl.split("/");
  const fileNameWithParams = urlParts[urlParts.length - 1] || "";
  const fileName = fileNameWithParams.split("?")[0];

  try {
    return normalizeFileName(decodeURIComponent(fileName));
  } catch {
    return normalizeFileName(fileName);
  }
};

const getMessageFileName = (message) => {
  const dataJsonMedia = getMediaPayloadFromDataJson(message);
  const cloudMedia = getCloudMediaFromDataJson(message);
  const body = normalizeFileName(message?.body);

  return [
    dataJsonMedia?.fileName,
    cloudMedia?.fileName,
    message?.mediaName,
    message?.originalname,
    message?.fileName,
    getFileNameFromContentDisposition(message?.mediaUrl),
    getFileNameFromUrl(message?.mediaUrl),
    FILE_NAME_REGEX.test(body) ? body : "",
  ].map(normalizeFileName).find(Boolean) || "";
};

const getMessageMimeType = (message) => {
  const dataJsonMedia = getMediaPayloadFromDataJson(message);
  const cloudMedia = getCloudMediaFromDataJson(message);
  return (
    dataJsonMedia?.mimetype ||
    cloudMedia?.mimeType ||
    message?.mimetype ||
    message?.mediaType ||
    ''
  ).toLowerCase();
};

const getFileTypeFromFileName = (fileName) => {
  if (!fileName) return "";
  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf": return "PDF";
    case "doc":
    case "docx": return "Word";
    case "xls":
    case "xlsx": return "Excel";
    case "ppt":
    case "pptx": return "PowerPoint";
    case "txt": return "Texto";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp": return "Imagen";
    case "mp4":
    case "avi":
    case "mov":
    case "webm": return "Video";
    case "mp3":
    case "wav":
    case "ogg": return "Audio";
    default: return ext?.toUpperCase() || "";
  }
};

const getFileTypeFromMimeType = (mimeType) => {
  if (!mimeType) return "";

  if (mimeType.includes("pdf")) return "PDF";
  if (mimeType.includes("excel") || mimeType.includes("sheet")) return "Excel";
  if (mimeType.includes("word") || mimeType.includes("document")) return "Word";
  if (mimeType.includes("powerpoint") || mimeType.includes("presentation")) return "PowerPoint";
  if (mimeType.includes("text")) return "Texto";
  if (mimeType.startsWith("image")) return "Imagen";
  if (mimeType.startsWith("video")) return "Video";
  if (mimeType.startsWith("audio")) return "Audio";
  if (mimeType === "application") return "";

  return mimeType.split("/")[1]?.toUpperCase() || mimeType.toUpperCase();
};

const getFileType = (fileName, mimeType) => (
  getFileTypeFromFileName(fileName) || getFileTypeFromMimeType(mimeType) || "Archivo"
);

const getFileIconConfig = (fileName, mimeType, defaultColor) => {
  const normalizedFileName = fileName?.toLowerCase() || "";
  const normalizedMimeType = mimeType?.toLowerCase() || "";

  if (normalizedMimeType.includes("pdf") || normalizedFileName.includes(".pdf")) {
    return { color: "#FF6B6B", icon: PdfIcon };
  }

  if (
    normalizedMimeType.includes("excel") ||
    normalizedMimeType.includes("sheet") ||
    normalizedFileName.includes(".xls") ||
    normalizedFileName.includes(".xlsx")
  ) {
    return { color: "#51CF66", icon: ExcelIcon };
  }

  if (
    normalizedMimeType.includes("word") ||
    normalizedMimeType.includes("document") ||
    normalizedFileName.includes(".doc") ||
    normalizedFileName.includes(".docx")
  ) {
    return { color: "#4DABF7", icon: WordIcon };
  }

  if (
    normalizedMimeType.includes("powerpoint") ||
    normalizedMimeType.includes("presentation") ||
    normalizedFileName.includes(".ppt") ||
    normalizedFileName.includes(".pptx")
  ) {
    return { color: "#FF922B", icon: PowerPointIcon };
  }

  if (normalizedMimeType.includes("text") || normalizedFileName.includes(".txt")) {
    return { color: "#868E96", icon: TextIcon };
  }

  return { color: defaultColor, icon: DescriptionIcon };
};

const formatFileSize = (fileSize) => {
  if (!fileSize) return "";

  const size = parseInt(fileSize, 10);
  if (Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${Math.round(size / (1024 * 1024))} MB`;
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    overflowX: "hidden", // NUNCA mostrar scroll horizontal
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    maxWidth: "100%", // Asegurar que no exceda el ancho del contenedor
    minWidth: 300,
    minHeight: 200,
  },
  scrollToLatestButton: {
    position: "absolute",
    left: "50%",
    bottom: 12,
    zIndex: 6,
    width: 44,
    height: 44,
    padding: 0,
    backgroundColor: "#FFFFFF",
    color: "#1D1C18",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.16)",
    transform: "translate(-50%, 80px)",
    opacity: 0,
    pointerEvents: "none",
    transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.2)",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "opacity 0.15s ease",
      transform: "translate(-50%, 0)",
    },
  },
  scrollToLatestButtonVisible: {
    transform: "translate(-50%, 0)",
    opacity: 1,
    pointerEvents: "auto",
  },

  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "16px 48px",
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",

    [theme.breakpoints.down("lg")]: {
      padding: "12px 10px",
    },

    [theme.breakpoints.down("xs")]: {
      padding: "8px 0",
    },
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  loadingMoreContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "16px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    margin: "8px auto",
    maxWidth: "200px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(0, 0, 0, 0.05)",
  },

  loadingMoreText: {
    marginLeft: "8px",
    fontSize: "14px",
    color: "#666",
    fontWeight: 500,
  },

  initialLoadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "300px",
  },

  initialLoadingText: {
    marginTop: "16px",
    fontSize: "16px",
    color: "#666",
    fontWeight: 500,
  },

  messageLeft: {
    margin: "2px 48px 2px 48px",
    maxWidth: 600,
    display: "block",
    backgroundColor: theme.palette.tabHeaderBackground,
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderRadius: 10,
    padding: "6px 8px 0 8px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    position: "relative",
    whiteSpace: "pre-wrap",
    transition: "background-color 0.15s ease",
    "&:hover": {
      "& #messageActionsButton": {
        display: "flex",
        position: "absolute",
        top: "5px",
        right: "5px",
      },
    },
    [theme.breakpoints.down("lg")]: {
      margin: "2px 40px",
      maxWidth: 450,
    },
    [theme.breakpoints.down("xs")]: {
      margin: "2px 40px",
    },
  },

  typingBubble: {
    margin: "2px 48px 8px 48px",
    alignSelf: "flex-start",
    flexShrink: 0,
    backgroundColor: theme.palette.tabHeaderBackground,
    borderRadius: 10,
    borderBottomLeftRadius: 0,
    padding: "10px 14px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    display: "flex",
    alignItems: "center",
    gap: 5,
    [theme.breakpoints.down("lg")]: {
      margin: "2px 40px 8px 40px",
    },
    [theme.breakpoints.down("xs")]: {
      margin: "2px 40px 8px 40px",
    },
  },

  typingDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: theme.mode === "light" ? "#8A8A86" : "#C8C8C4",
    animation: "$typingBounce 1.2s infinite ease-in-out",
    "&:nth-child(2)": {
      animationDelay: "0.15s",
    },
    "&:nth-child(3)": {
      animationDelay: "0.3s",
    },
  },

  "@keyframes typingBounce": {
    "0%, 60%, 100%": {
      transform: "translateY(0)",
      opacity: 0.4,
    },
    "30%": {
      transform: "translateY(-4px)",
      opacity: 1,
    },
  },

  recordingMic: {
    fontSize: 18,
    display: "block",
    animation: "$micPulse 1.05s infinite ease-in-out",
  },

  "@keyframes micPulse": {
    "0%, 100%": {
      color: theme.mode === "light" ? "#8A8A86" : "#A8A8A4",
      opacity: 0.5,
    },
    "50%": {
      color: "#F1B000",
      opacity: 1,
    },
  },

  messageRight: {
    margin: "2px 48px",
    minWidth: 50,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? '#FFE397' : '#F1B000',
    color: '#1B1B18',
    alignSelf: "flex-end",
    borderRadius: 10,
    padding: "6px 8px 0 8px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "background-color 0.15s ease",
    "&:hover": {
      "& #messageActionsButton": {
        display: "flex",
        position: "absolute",
        top: 0,
        right: 0,
      },
    },
    [theme.breakpoints.down("lg")]: {
      margin: "2px 40px",
      maxWidth: 450,
    },
    [theme.breakpoints.down("xs")]: {
      margin: "2px 40px",
    },
  },

  "@keyframes quotedHighlight": {
    "0%": { boxShadow: "0 0 0 0 rgba(241, 176, 0, 0.55)" },
    "70%": { boxShadow: "0 0 0 12px rgba(241, 176, 0, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(241, 176, 0, 0)" },
  },
  highlightedMessage: {
    animation: "$quotedHighlight 1.4s ease",
    outline: "2px solid #F1B000",
    outlineOffset: 2,
  },
  quotedClickable: {
    cursor: "pointer",
    "&:hover": {
      filter: "brightness(0.96)",
    },
  },
  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },
  avatarLeft: {
    position: 'absolute',
    bottom: 0,
    left: -45,
    width: 40,
    height: 40,
    zIndex: 1, // Debajo de los botones de respuesta
    [theme.breakpoints.down("xs")]: {
      left: -42,
      width: 35,
      height: 35,
    },
    color: '#1B1B18',
    backgroundColor: theme.mode === 'light' ? '#FFE397' : '#F1B000',
  },

  avatarRight: {
    position: 'absolute',
    bottom: 0,
    right: -45,
    width: 40,
    height: 40,
    zIndex: 1, // Debajo de los botones de respuesta
    [theme.breakpoints.down("xs")]: {
      right: -42,
      width: 35,
      height: 35,
    },
    color: '#1B1B18',
    backgroundColor: theme.mode === 'light' ? '#FFE397' : '#F1B000',
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  // Nuevos estilos para mensajes citados mejorados
  quotedHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "4px",
    flexWrap: "wrap",
  },

  quotedSenderName: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6bcbef",
    minWidth: "0",
    flex: "0 1 auto",
  },

  quotedMessageType: {
    fontSize: "11px",
    fontWeight: "500",
    color: "#666",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    padding: "2px 6px",
    borderRadius: "8px",
    flex: "0 0 auto",
  },

  quotedTimestamp: {
    fontSize: "10px",
    color: "#999",
    marginLeft: "auto",
    flex: "0 0 auto",
  },

  quotedContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },

  quotedImagePreview: {
    flex: "0 0 auto",
    width: "40px",
    height: "40px",
    borderRadius: "4px",
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
  },

  quotedImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  quotedText: {
    fontSize: "13px",
    color: "#333",
    lineHeight: "1.3",
    flex: "1 1 auto",
    minWidth: "0",
    wordBreak: "break-word",
  },

  quotedFileName: {
    fontSize: "12px",
    color: "#666",
    fontStyle: "italic",
    flex: "1 1 auto",
    minWidth: "0",
    wordBreak: "break-word",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  messageAudioMedia: {
    objectFit: "cover",
    width: 250,
    height: 50,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.text.primary,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "auto",
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
    margin: "12px auto",
    borderRadius: 12,
    boxShadow: "none",
  },

  dailyTimestampText: {
    color: theme.mode === "light" ? "#808888" : "#AEADAC",
    padding: "4px 12px",
    alignSelf: "center",
    marginLeft: 0,
    fontSize: 12,
    fontWeight: 500,
  },

  messageMetaIcon: {
    fontSize: 14,
    marginLeft: 2,
    marginRight: 1,
    verticalAlign: "middle",
    opacity: 0.85,
  },
  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    backgroundColor: "inherit",
    margin: "4px 0",
    maxWidth: "100%",
    position: "relative",
  },
  imageLocation: {
    position: 'relative',
    width: '100%',
    height: 80,
    borderRadius: 5
  },
  imageModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  imageModalContent: {
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalImage: {
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  closeButton: {
    position: 'absolute',
    top: '-40px',
    right: '-40px',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
  },
  downloadButton: {
    position: 'absolute',
    top: '-40px',
    right: '10px',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
  },
  navigationButton: {
    color: '#fff',
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '12px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
  },
  prevButton: {
    left: '-60px',
  },
  nextButton: {
    right: '-60px',
    '& svg': {
      transform: 'rotate(180deg)'
    }
  },
  // Opción 1 (activa): violeta suave — buen contraste sin saturar
  whisperMessage: {
    backgroundColor: theme.mode === "light"
      ? "rgba(128, 0, 128, 0.15)"
      : "rgba(255, 255, 255, 0.1)",
    borderLeft: theme.mode === "light" ? "4px solid #7c3aed" : "4px solid #a78bfa",
    position: "relative",
    borderRadius: 8,
    boxShadow: theme.mode === "light"
      ? "0 2px 4px rgba(128, 0, 128, 0.2)"
      : "0 2px 8px rgba(0, 0, 0, 0.25)",
    color: theme.mode === "light" ? "#4c1d95" : "#f3e8ff",
    alignSelf: "flex-end",
    "& $timestamp": {
      position: "absolute",
      color: theme.mode === "light" ? "#6b21a8" : "#c4b5fd",
    },
    "& $messageContactName": {
      color: theme.mode === "light" ? "#6b21a8" : "#ddd6fe",
    },
  },
  // Opción 2: púrpura profundo — más contraste, estilo "nota confidencial"
  // whisperMessage: {
  //   backgroundColor: theme.mode === 'light' ? 'rgba(128, 0, 128, 0.15)' : 'rgba(59, 7, 100, 0.55)',
  //   borderLeft: theme.mode === 'light' ? '4px solid purple' : '4px solid #7c3aed',
  //   color: theme.mode === 'light' ? '#4c1d95' : '#faf5ff',
  // },
  // whisperText: { fontStyle: 'italic', color: theme.mode === 'light' ? 'rgba(76, 29, 149, 0.95)' : '#faf5ff' },
  // whisperIndicator: { color: theme.mode === 'light' ? '#6b21a8' : '#c4b5fd', ... },

  // Opción 3: fucsia sutil — más llamativo, acento moderno
  // whisperMessage: {
  //   backgroundColor: theme.mode === 'light' ? 'rgba(128, 0, 128, 0.15)' : 'rgba(134, 25, 143, 0.35)',
  //   borderLeft: theme.mode === 'light' ? '4px solid purple' : '4px solid #e879f9',
  //   color: theme.mode === 'light' ? '#4c1d95' : '#fdf4ff',
  // },
  // whisperText: { fontStyle: 'italic', color: theme.mode === 'light' ? 'rgba(76, 29, 149, 0.95)' : '#fdf4ff' },
  // whisperIndicator: { color: theme.mode === 'light' ? '#6b21a8' : '#f0abfc', ... },

  whisperText: {
    fontStyle: "italic",
    color: theme.mode === "light" ? "rgba(76, 29, 149, 0.95)" : "#f3e8ff",
  },

  whisperIndicator: {
    display: "flex",
    alignItems: "center",
    fontSize: 12,
    color: theme.mode === "light" ? "#6b21a8" : "#ddd6fe",
    marginBottom: 4,
    fontWeight: "bold",
  },
  contactCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    width: 330,
    maxWidth: 330,
    margin: '0 auto',
  },
  contactHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #E0E0E0',
  },
  contactAvatar: {
    width: 60,
    height: 60,
    marginRight: 20,
    backgroundColor: '#DFE5E7',
  },
  contactInfo: {
    flex: 1,
  },
  contactName: {
    fontWeight: 600,
    fontSize: '18px',
    marginBottom: 6,
    color: '#000000',
  },
  contactDescription: {
    fontSize: '15px',
    color: '#666666',
    marginBottom: 0,
  },
  contactFooter: {
    backgroundColor: '#F8F9FA',
    padding: '14px 0',
    textAlign: 'center',
    borderTop: '1px solid #E0E0E0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E8F5E9',
    },
  },
  contactAction: {
    color: '#00897B',
    fontWeight: 600,
    fontSize: '14px',
    letterSpacing: '0.5px',
    padding: '4px 14px',
    display: 'inline-block',
  },
  fileContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    maxWidth: '320px',
    minWidth: '280px',
    padding: '16px 16px 28px 16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  fileIconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px',
    flexShrink: 0,
  },
  fileIcon: {
    fontSize: '24px',
    color: '#ffffff',
  },
  fileInfo: {
    flexGrow: 1,
    minWidth: 0,
    marginRight: '12px',
  },
  fileName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '15px',
    lineHeight: 1.3,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '2px',
  },
  fileSize: {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  fileType: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    padding: '2px 8px',
    borderRadius: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  downloadIconButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff',
      transform: 'scale(1.05)',
    },
  },
  downloadIcon: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    marginLeft: '8px',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  messageMediaContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
  },
  imageCaption: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    wordBreak: 'break-word',
    backgroundColor: '#f8f9fa',
    borderRadius: '0 0 4px 4px',
    borderTop: '1px solid #e9ecef',
  },
  audioContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px 16px 28px 16px',
    maxWidth: '280px',
    minWidth: '260px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    position: 'relative',
    margin: '4px 0',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  audioPlayer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
  },
  audioPlayButton: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scale(1.05)',
    },
    '& svg': {
      color: '#ffffff',
      fontSize: '20px',
    },
  },
  audioWaveform: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  audioProgress: {
    width: '100%',
    height: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  audioProgressFill: {
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
    transition: 'width 0.1s ease',
  },
  audioDuration: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  audioWaves: {
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
    height: '24px',
    marginBottom: '4px',
    width: '100%',
    overflow: 'hidden',
  },
  audioWave: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    borderRadius: '1px',
    transition: 'background-color 0.2s ease',
    minWidth: '2px',
    maxWidth: '4px',
  },
  audioWaveActive: {
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 0 2px ${theme.palette.primary.main}`,
  },
  audioCaption: {
    width: '100%',
    marginTop: '8px',
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  videoContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
  },
  videoCaption: {
    width: '100%',
    padding: '8px',
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    wordBreak: 'break-word',
    backgroundColor: '#f8f9fa',
    borderRadius: '0 0 4px 4px',
    borderTop: '1px solid #e9ecef',
  },
  pendingMessage: {
    // ELIMINADO: Clase que causaba el efecto shimmer/brillado
    // Ya no aplicamos ningún efecto visual especial a mensajes pendientes
    // Solo se muestra el ícono de reloj en renderMessageAck
  },
  failedMessage: {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    borderLeft: `4px solid #f44336`,
    position: 'relative',
  },

  // Área clickeable invisible para respuesta rápida - mensajes recibidos
  quickReplyAreaLeft: {
    position: "absolute",
    top: "0",
    left: "-40vw",
    width: "40vw",
    height: "100%",
    zIndex: 3,
    cursor: "pointer",
    backgroundColor: "transparent",
    display: "block", // Siempre visible
    "&:hover": {
      "& + div": { // Activa el botón hermano siguiente
        display: "flex !important",
        backgroundColor: `white !important`,
        color: "gray !important",
        transform: "translateY(-50%) scale(1.05) !important",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2) !important",
      },
    },
    [theme.breakpoints.down("xs")]: {
      left: "-45px",
      width: "45px",
    },
  },

  // Área clickeable invisible para respuesta rápida - mensajes enviados
  quickReplyAreaRight: {
    position: "absolute",
    top: "0",
    right: "-33vw",
    width: "33vw",
    height: "100%",
    zIndex: 3,
    cursor: "pointer",
    backgroundColor: "transparent",
    display: "block", // Siempre visible
    "&:hover": {
      "& + div": { // Activa el botón hermano siguiente
        display: "flex !important",
        backgroundColor: `white !important`,
        color: "gray !important",
        transform: "translateY(-50%) scaleX(-1) scale(1.05) !important",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2) !important",
      },
    },
    [theme.breakpoints.down("xs")]: {
      right: "-45px",
      width: "45px",
    },
  },

  // Nuevo estilo para el botón de respuesta rápida - mensajes recibidos (izquierda)
  quickReplyButtonLeft: {
    display: "none",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    left: "-40px", // Fuera del mensaje, en el espacio en blanco
    color: theme.palette.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    zIndex: 2,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    pointerEvents: "none", // El área padre maneja los clicks
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: "18px",
    },
    // Responsive para dispositivos móviles
    [theme.breakpoints.down("xs")]: {
      width: "28px",
      height: "28px",
      left: "-35px",
      "& svg": {
        fontSize: "16px",
      },
    },
  },

  // Nuevo estilo para el botón de respuesta rápida - mensajes enviados (derecha)
  quickReplyButtonRight: {
    display: "none",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "-40px", // Fuera del mensaje, en el espacio en blanco
    color: theme.palette.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    zIndex: 2,
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    pointerEvents: "none", // El área padre maneja los clicks
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: "18px",
    },
    // Responsive para dispositivos móviles
    [theme.breakpoints.down("xs")]: {
      width: "28px",
      height: "28px",
      right: "-35px",
      "& svg": {
        fontSize: "16px",
      },
    },
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_MESSAGES":
      if (!Array.isArray(action.payload)) {
        return state;
      }
      const uniqueMessages = action.payload.filter((msg, index, self) => {
        return index === self.findIndex((m) => {
          if (msg.id && m.id === msg.id) return true;
          if (msg.tempId && m.tempId === msg.tempId) return true;
          return false;
        });
      });
      return uniqueMessages;
    case "ADD_MESSAGE":
      {
        const msg = action.payload;
        const exists = state.some(m => {
          if (msg.id && m.id === msg.id) return true;
          if (msg.tempId && m.tempId === msg.tempId) return true;
          return false;
        });
        if (exists) return state;
        return [...state, msg];
      }
    case "RECONCILE_MESSAGE":
      {
        const { tempId, realMessage } = action.payload;
        // Reemplaza mensaje optimista por real, conservando flags locales
        let replaced = false;
        const next = state.map(m => {
          if (m.tempId && m.tempId === tempId) {
            replaced = true;
            return { ...realMessage, _optimistic: false, tempId };
          }
          // Si por carrera llegó el real antes de que el optimistic dispare
          if (realMessage && m.id === realMessage.id) {
            replaced = true;
            return { ...realMessage, _optimistic: false };
          }
          return m;
        });
        if (replaced) return next;
        // No se encontró el optimista → agregar como nuevo
        if (!state.some(m => m.id === realMessage.id)) {
          return [...state, { ...realMessage, _optimistic: false }];
        }
        return state;
      }
    case "UPDATE_MESSAGE":
      return state.map(m => m.id === action.payload.id ? action.payload : m);
    case "UPDATE_MESSAGE_ACK":
      return state.map((m) => {
        if (m.id !== action.payload.id) {
          return m;
        }
        const next = { ...m, ack: action.payload.ack };
        if (action.payload.dataJson !== undefined) {
          next.dataJson = action.payload.dataJson;
        } else if (action.payload.error) {
          try {
            const base =
              typeof m.dataJson === "string"
                ? JSON.parse(m.dataJson)
                : m.dataJson && typeof m.dataJson === "object"
                  ? m.dataJson
                  : {};
            next.dataJson = JSON.stringify({
              ...(base && typeof base === "object" ? base : {}),
              error: action.payload.error,
            });
          } catch (_) {
            next.dataJson = JSON.stringify({ error: action.payload.error });
          }
        }
        return next;
      });
    case "MARK_MESSAGE_FAILED":
      return state.map(m => (m.tempId && m.tempId === action.payload.tempId) || m.id === action.payload.id
        ? { ...m, status: "failed", ack: -1, _optimistic: false }
        : m
      );
    case "REMOVE_OPTIMISTIC_MESSAGE":
      return state.filter(m => {
        if (action.payload.tempId && m.tempId === action.payload.tempId) return false;
        if (action.payload.id && m.id === action.payload.id && m._optimistic) return false;
        return true;
      });
    case "RESET":
      return [];
    default:
      return state;
  }
};

const MessagesList = ({ ticket, ticketId, isGroup, quickMessageModalOpen, emojiModalOpen, onRegisterOptimisticHandler, onTicketMetaUpdate }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { fetchUserData } = useContext(StaticResourcesContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  // 🔄 Cache local de mensajes (implementación directa sin contexto IndexedDB)
  const [messagesCache, setMessagesCache] = useState({});
  const [loadingMessages, setLoadingMessages] = useState({});

  const componentId = useMemo(() => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      return `messageList-${timestamp}-${random}`;
  }, []);

  const disableCloudMessageEditDelete = useMemo(
    () => isCloudMessageEditDeleteDisabled(resolveTicketChannel(ticket)),
    [ticket],
  );
  
  // 🔄 Función directa para cargar mensajes desde la API
  const fetchTicketMessages = async (ticketId, pageNumber = 1, forceUpdate = false) => {
    try {
      console.log(`🔄 fetchTicketMessages DIRECTO: ticket=${ticketId}, page=${pageNumber}, force=${forceUpdate}`);

      // Si no es forzado y es página 1, verificar cache primero
      if (!forceUpdate && pageNumber === 1) {
        const cachedData = messagesCache[ticketId];
        if (cachedData && cachedData.messages && cachedData.messages.length > 0) {
          console.log(`📦 Usando cache local para ticket ${ticketId}: ${cachedData.messages.length} mensajes`);
          return {
            messages: cachedData.messages,
            hasMore: cachedData.hasMore || false,
            totalCount: cachedData.totalCount || cachedData.messages.length
          };
        }
      }

      console.log(`🌐 Cargando DIRECTAMENTE desde servidor: ticket=${ticketId}, página=${pageNumber}`);

      const { data } = await api.get(`/messages/${ticketId}`, {
        params: {
          pageNumber: pageNumber,
          limit: 20
        }
      });

      if (pageNumber === 1 && data?.ticket && typeof onTicketMetaUpdate === "function") {
        onTicketMetaUpdate(data.ticket);
      }

      // Nuevo contrato: el backend devuelve array plano de mensajes
      let messages = [];
      if (Array.isArray(data)) {
        messages = data;
      } else if (data && Array.isArray(data.messages)) {
        messages = data.messages;
      } else {
        console.warn(`⚠️ No se recibieron mensajes del servidor`);
        return { messages: [], hasMore: false, totalCount: 0 };
      }

      console.log(`✅ Servidor devolvió ${messages.length} mensajes`);

      const orderedMessages = messages
        .filter(msg => msg && msg.id)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      let finalMessages = orderedMessages;
      if (pageNumber > 1) {
        const cachedData = messagesCache[ticketId];
        if (cachedData && cachedData.messages) {
          const existingIds = new Set(cachedData.messages.map(msg => msg.id));
          const newMessages = orderedMessages.filter(msg => !existingIds.has(msg.id));
          finalMessages = [...cachedData.messages, ...newMessages]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        }
      }

      const newCacheData = {
        messages: finalMessages,
        hasMore: false,
        lastPage: pageNumber,
        totalCount: finalMessages.length
      };

      setMessagesCache(prev => ({
        ...prev,
        [ticketId]: newCacheData
      }));

      console.log(`✅ Carga DIRECTA completada: ${finalMessages.length} mensajes total`);

      return {
        messages: finalMessages,
        hasMore: false,
        totalCount: finalMessages.length
      };

    } catch (err) {
      console.error(`❌ Error cargando mensajes DIRECTO: ticket=${ticketId}, page=${pageNumber}`, err);
      toastError(err);
      return { messages: [], hasMore: false, totalCount: 0 };
    }
  };
  
  const updateTicketMessage = (ticketId, messageId, updatedData) => {
    console.log(`🔄 Actualizando mensaje DIRECTO: ticket=${ticketId}, messageId=${messageId}`);
    setMessagesCache(prev => {
      const cachedData = prev[ticketId];
      if (!cachedData || !cachedData.messages) return prev;
      
      const updatedMessages = cachedData.messages.map(msg => 
        msg.id === messageId ? { ...msg, ...updatedData } : msg
      );
      
      return {
        ...prev,
        [ticketId]: {
          ...cachedData,
          messages: updatedMessages
        }
      };
    });
  };
  
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const lastMessageRef = useRef();
  const messagesEndRef = useRef();
  const messagesContainerRef = useRef();
  const currentTicketId = useRef(ticketId);
  const lastScrollHeight = useRef(0);
  const pendingScrollToMessageId = useRef(null);
  const pendingScrollAttempts = useRef(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const isSmOrLess = useMediaQuery(theme.breakpoints.down('xs'));
  const [currentUser, setCurrentUser] = useState(user);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);

  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [chatImages, setChatImages] = useState([]);

  // Añadir estado para controlar el modal de contacto
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactData, setContactData] = useState(null);
  
  // 🔄 Estado para detectar cuando el usuario regresa a la app
  const [wasPageHidden, setWasPageHidden] = useState(false);
  const [contactPresence, setContactPresence] = useState(null);
  const typingTimeoutRef = useRef(null);
  const typingBubbleRef = useRef(null);
  const ticketIdRef = useRef(ticketId);
  ticketIdRef.current = ticketId;
  const TYPING_IDLE_MS = 8000;

  const stopContactPresence = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setContactPresence(null);
  }, []);

  const startContactPresence = useCallback((mode) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setContactPresence(mode);
    typingTimeoutRef.current = setTimeout(() => {
      setContactPresence(null);
      typingTimeoutRef.current = null;
    }, TYPING_IDLE_MS);
  }, []);

  // Hook para controlar el overflow del body cuando el modal de respuestas rápidas está abierto
  useEffect(() => {
    if (quickMessageModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [quickMessageModalOpen]);

  // 🚀 OPTIMIZADO: Obtener información completa del usuario desde cache
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Usar cache inteligente en lugar de llamada directa
        const userData = await fetchUserData(user.id);
        setCurrentUser(userData || user); // Fallback al usuario del contexto
        console.log(`✅ Datos de usuario cargados desde cache para ${userData?.name || user?.name}`);
      } catch (error) {
        console.error("Error obteniendo información del usuario:", error);
        setCurrentUser(user); // Fallback al usuario del contexto
      }
    };

    if (user?.id) {
      fetchUserInfo();
    }
  }, [user?.id]); // 🔧 FIXED: Removida fetchUserData de las dependencias para evitar bucle infinito

  // Función para abrir el modal con los datos del contacto
  const handleOpenContactModal = (name, phoneNumber) => {
    // Asegurarse de que el número tenga el formato correcto (con prefijo +)
    let formattedNumber = phoneNumber || "";
    
    // Si el número no está vacío y no comienza con +, añadirlo
    if (formattedNumber && !formattedNumber.startsWith("+")) {
      formattedNumber = "+" + formattedNumber;
    }
    
    // Si aún está vacío, inicializar con + para cumplir con la validación
    if (!formattedNumber) {
      formattedNumber = "+";
    }
    
    setContactData({
      name: name || "Contacto",
      number: formattedNumber,
      email: "",
    });
    setContactModalOpen(true);
  };

  // Función para cerrar el modal de contacto
  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setContactData(null);
  };

  const isVCard = (message) => {
    return message.includes('BEGIN:VCARD');
  };

  const vCard = (message) => {
    // Extraer nombre y número de teléfono del vCard
    let name = '';
    let phoneNumber = '';
    
    try {
      // Intentar extraer el nombre de diferentes formas según el formato de vCard
      if (message.includes("FN:")) {
        name = message.substring(message.indexOf("FN:") + 3, message.indexOf("\n", message.indexOf("FN:")));
      } else if (message.includes("N:")) {
        const nameSection = message.substring(message.indexOf("N:") + 2);
        const nameParts = nameSection.substring(0, nameSection.indexOf("\n")).split(";");
        // Formato típico es N:Apellido;Nombre;;;
        name = (nameParts[1] && nameParts[0]) 
          ? `${nameParts[1].trim()} ${nameParts[0].trim()}` 
          : (nameParts[0] || 'Contacto');
      }
      
      // Limpiar el nombre
      name = name.trim() || 'Contacto';
      
      // Extraer número telefónico - Búsqueda mejorada
      const phonePatterns = [
        /TEL[^:]*:([\+\d\s\-\(\)]+)/i,     // Busca TEL: o TEL;X: seguido de número
        /TEL[^:]*;CELL:([\+\d\s\-\(\)]+)/i,  // Busca TEL;CELL:
        /TEL[^:]*;VOICE:([\+\d\s\-\(\)]+)/i, // Busca TEL;VOICE:
        /TEL[^:]*;WORK:([\+\d\s\-\(\)]+)/i,  // Busca TEL;WORK:
        /TEL[^:]*;HOME:([\+\d\s\-\(\)]+)/i,  // Busca TEL;HOME:
        /TEL[^:]*;MOBILE:([\+\d\s\-\(\)]+)/i // Busca TEL;MOBILE:
      ];
      
      for (const pattern of phonePatterns) {
        const match = message.match(pattern);
        if (match && match[1]) {
          phoneNumber = match[1].trim();
          // Limpiar el número de teléfono (eliminar caracteres no deseados)
          phoneNumber = phoneNumber.replace(/[^\+\d]/g, '');
          break;
        }
      }
      
      // Si no se encontró ningún número con los patrones, intentar el método anterior
      if (!phoneNumber) {
        if (message.includes("TEL:")) {
          phoneNumber = message.substring(message.indexOf("TEL:") + 4);
          phoneNumber = phoneNumber.substring(0, phoneNumber.indexOf("\n")).trim();
        } else if (message.includes("TEL;")) {
          const telSection = message.substring(message.indexOf("TEL;"));
          phoneNumber = telSection.substring(telSection.indexOf(":") + 1, telSection.indexOf("\n")).trim();
        }
        // Limpiar el número de teléfono (eliminar caracteres no deseados)
        phoneNumber = phoneNumber.replace(/[^\+\d]/g, '');
      }

      // Añadir un log para depuración
      console.log('Renderizando vCard con nombre:', name, 'y teléfono:', phoneNumber);
      
      return (
        <div className={classes.contactCard}>
          <div className={classes.contactHeader}>
            <Avatar className={classes.contactAvatar}>
              <PersonIcon style={{ fontSize: 40, color: '#919191' }} />
            </Avatar>
            <div className={classes.contactInfo}>
              <Typography
                component="h4"
                variant="body1"
                className={classes.contactName}
              >
                {name}
              </Typography>
              <Typography
                component="p"
                variant="body2"
                className={classes.contactDescription}
              >
                {phoneNumber || 'Contacto de WhatsApp'}
              </Typography>
            </div>
          </div>
          <div 
            className={classes.contactFooter} 
            onClick={(e) => {
              e.stopPropagation();
              console.log('Footer clicked, opening modal for:', name, phoneNumber);
              handleOpenContactModal(name, phoneNumber);
            }}
          >
            <Typography
              component="span"
              variant="body2"
              className={classes.contactAction}
            >
              AGREGAR CONTACTO
            </Typography>
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error al renderizar vCard:", error);
      return (
        <div className={classes.contactCard}>
          <div className={classes.contactHeader}>
            <Avatar className={classes.contactAvatar}>
              <PersonIcon style={{ fontSize: 40, color: '#919191' }} />
            </Avatar>
            <div className={classes.contactInfo}>
              <Typography component="h4" variant="body1" className={classes.contactName}>
                Contacto
              </Typography>
            </div>
          </div>
          <div 
            className={classes.contactFooter}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenContactModal("Contacto", "");
            }}
          >
            <Typography component="span" variant="body2" className={classes.contactAction}>
              AGREGAR CONTACTO
            </Typography>
          </div>
        </div>
      );
    }
  };

  const resolveQuotedMsg = (quotedMsgId, messagesList, socketQuotedMsg) => {
    if (socketQuotedMsg) return socketQuotedMsg;
    if (!quotedMsgId) return null;
    return (
      messagesList.find(
        (msg) => msg.id === quotedMsgId || msg.tempId === quotedMsgId,
      ) || null
    );
  };

  useEffect(() => {
    let isMounted = true;
    let loadingTimeout;

    const loadInitialMessages = async () => {
      if (!ticketId) {
        console.log('⚠️ No hay ticketId, saltando carga de mensajes');
        return;
      }

      try {
        console.log(`🔄 Iniciando carga de mensajes para ticket ${ticketId}`);
        
        // Verificar si es un ticket diferente
        const isNewTicket = currentTicketId.current !== ticketId;
        
        if (isNewTicket) {
          console.log(`🆕 Cambiando a nuevo ticket: ${currentTicketId.current} -> ${ticketId}`);
          dispatch({ type: "RESET" });
          setPageNumber(1);
          setHasMore(false);
          currentTicketId.current = ticketId;
          setInitialLoading(true);
          pendingScrollToMessageId.current = null;
          pendingScrollAttempts.current = 0;
          setHighlightedMessageId(null);
          setShowScrollToLatest(false);
          stopContactPresence();
        }

        // 🚀 CARGA INICIAL DE MENSAJES CON PAGINACIÓN Y TIMEOUT
        console.log(`🔄 Cargando mensajes iniciales para ticket ${ticketId}`);
        setLoadingMessages(prev => ({ ...prev, [ticketId]: true }));
        
        // Timeout de seguridad para evitar carga infinita
        loadingTimeout = setTimeout(() => {
          if (isMounted) {
            console.warn(`⏰ Timeout cargando mensajes para ticket ${ticketId}`);
            setLoadingMessages(prev => ({ ...prev, [ticketId]: false }));
            setInitialLoading(false);
          }
        }, 10000); // 10 segundos de timeout
        
        // Cargar primera página de mensajes (forzar para obtener datos frescos)
        const data = await fetchTicketMessages(ticketId, 1, true);
        
        // Limpiar timeout si la carga fue exitosa
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
        
        if (isMounted && currentTicketId.current === ticketId && data?.messages) {
          console.log(`✅ Cargados ${data.messages.length} mensajes iniciales, hasMore: ${data.hasMore}`);
          
          // Cargar mensajes en la vista
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore || false);
          setPageNumber(1);
          
          // 🔄 Verificar mensajes nuevos en background si hay cache
          if (data.messages.length > 0) {
            setTimeout(() => {
              if (isMounted && currentTicketId.current === ticketId) {
                checkForNewMessages(ticketId, { messages: data.messages });
              }
            }, 500);
          }
          
          // Scroll suave al final después de renderizar
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (isMounted && currentTicketId.current === ticketId) {
                scrollToBottom();
              }
            }, 100);
          });
        } else if (isMounted && currentTicketId.current === ticketId) {
          console.warn(`⚠️ No se cargaron mensajes para ticket ${ticketId}`);
          dispatch({ type: "LOAD_MESSAGES", payload: [] });
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error cargando mensajes:", err);
        if (isMounted) {
          // Solo mostrar error si no es un problema de red temporal
          if (!err.message?.includes('Network Error') && !err.message?.includes('timeout')) {
            toastError("Error al cargar los mensajes");
          }
        }
      } finally {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        if (isMounted) {
          setLoadingMessages(prev => ({ ...prev, [ticketId]: false }));
          setInitialLoading(false);
        }
      }
    };

    // 🔄 Función para verificar mensajes nuevos (adaptada para carga directa)
    const checkForNewMessages = async (ticketId, cachedData) => {
      try {
        const lastCachedMessage = cachedData.messages[cachedData.messages.length - 1];
        if (!lastCachedMessage) return;

        const lastMessageDate = lastCachedMessage.createdAt;
        console.log(`🔍 Verificando mensajes nuevos después de: ${lastMessageDate}`);

        const { data } = await api.get(`/messages/${ticketId}/newer`, {
          params: {
            afterDate: lastMessageDate,
            limit: 50
          }
        });

        const newMessages = Array.isArray(data)
          ? data
          : (Array.isArray(data?.messages) ? data.messages : []);

        if (newMessages.length > 0) {
          console.log(`📨 Encontrados ${newMessages.length} mensajes nuevos`);

          const allMessages = [...cachedData.messages, ...newMessages]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .filter((msg, index, self) => index === self.findIndex((m) => m.id === msg.id));

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: allMessages });

            const updatedCacheData = {
              ...cachedData,
              messages: allMessages,
              totalCount: allMessages.length
            };

            setMessagesCache(prev => ({
              ...prev,
              [ticketId]: updatedCacheData
            }));

            if (isNearBottom()) {
              setTimeout(() => scrollToBottom(), 100);
            }
          }
        } else {
          console.log(`✅ No hay mensajes nuevos para ticket ${ticketId}`);
        }
      } catch (err) {
        console.warn("Error verificando mensajes nuevos:", err);
      }
    };

    // Pequeña pausa para asegurar que el contexto esté sincronizado
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        loadInitialMessages();
      }
    }, 100);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
  }, [ticketId]); // SOLO ticketId como dependencia

  // 🔄 Detector de visibilidad para sincronizar cuando el usuario regresa
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWasPageHidden(true);
        console.log(`👁️ Usuario salió de la app - ticket: ${ticketId}`);
      } else if (wasPageHidden && ticketId) {
        console.log(`👁️ Usuario regresó a la app - verificando mensajes nuevos para ticket: ${ticketId}`);
        setWasPageHidden(false);
        
        // Verificar mensajes nuevos cuando el usuario regresa
        const cachedData = messagesCache[ticketId];
        if (cachedData?.messages?.length > 0) {
          setTimeout(() => {
            checkForNewMessagesOnReturn(ticketId, cachedData);
          }, 500);
        }
      }
    };

    const checkForNewMessagesOnReturn = async (ticketId, cachedData) => {
      try {
        const lastCachedMessage = cachedData.messages[cachedData.messages.length - 1];
        if (!lastCachedMessage) return;

        const lastMessageDate = lastCachedMessage.createdAt;
        console.log(`🔍 [RETURN] Verificando mensajes nuevos después de: ${lastMessageDate}`);

        const { data } = await api.get(`/messages/${ticketId}/newer`, {
          params: {
            afterDate: lastMessageDate,
            limit: 50
          }
        });

        const newMessages = Array.isArray(data)
          ? data
          : (Array.isArray(data?.messages) ? data.messages : []);

        if (newMessages.length > 0) {
          console.log(`📨 [RETURN] Encontrados ${newMessages.length} mensajes nuevos al regresar`);

          const allMessages = [...cachedData.messages, ...newMessages]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .filter((msg, index, self) => index === self.findIndex((m) => m.id === msg.id));

          dispatch({ type: "LOAD_MESSAGES", payload: allMessages });

          setMessagesCache(prev => ({
            ...prev,
            [ticketId]: {
              ...cachedData,
              messages: allMessages,
              totalCount: allMessages.length
            }
          }));

          setTimeout(() => scrollToBottom(), 200);

          console.log(`✨ ${newMessages.length} mensajes nuevos cargados`);
        } else {
          console.log(`✅ [RETURN] No hay mensajes nuevos al regresar`);
        }
      } catch (err) {
        console.warn("Error verificando mensajes nuevos al regresar:", err);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [ticketId, wasPageHidden, messagesCache]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    // 🆕 El join/leave del room del ticket vive en useTicketRoom()
    // (montado en TicketsCustom / TicketsAdvanced). Este useEffect solo
    // registra listeners de mensajes.

    // 🔧 ARREGLO: Eliminado handleInstantMessage duplicado - ahora todo se maneja en handleMessage unificado

    // 🔧 NUEVO: Listener específico para eliminación de mensajes
    const handleMessageDeleted = (data) => {
      console.log('🗑️ handleMessageDeleted recibido:', data); // DEBUG
      
      if (data.messageId && data.ticketId === currentTicketId.current) {
        console.log('✅ Procesando eliminación de mensaje:', data.messageId, 'en ticket actual:', currentTicketId.current); // DEBUG
        
        // Buscar el mensaje en la lista actual y marcarlo como eliminado
        const updatedMessages = messagesList.map(msg => {
          if (msg.id === data.messageId) {
            return {
              ...msg,
              isDeleted: true,
              body: "Este mensaje fue eliminado"
            };
          }
          return msg;
        });

        // Actualizar cache si existe
        if (messagesCache[data.ticketId]) {
          updateTicketMessage(data.ticketId, data.messageId, {
            isDeleted: true,
            body: "Este mensaje fue eliminado"
          });
        }

        // Aplicar cambios al estado local
        dispatch({ type: "LOAD_MESSAGES", payload: updatedMessages });
        
        console.log('✅ Mensaje marcado como eliminado en el frontend:', data.messageId); // DEBUG
      } else {
        console.log('⚠️ Eliminación de mensaje para ticket diferente:', data.ticketId, 'vs actual:', currentTicketId.current); // DEBUG
      }
    };

    const handleMessageAck = (data) => {
      console.log('📨 message:ack recibido:', data);
      if (data.messageId && data.ticketId === currentTicketId.current) {
        console.log('✅ Procesando ack de mensaje:', data.messageId, 'en ticket actual:', currentTicketId.current); // DEBUG
        dispatch({
          type: "UPDATE_MESSAGE_ACK",
          payload: {
            id: data.messageId,
            ack: data.ack,
            ...(data.error ? { error: data.error } : {}),
          },
        });
        if (data.ack === -1 && data.error) {
          if (isPaymentMethodRequiredError(data.error)) {
            openCloudPaymentRequiredDialog();
          } else {
            toast.error(translateBackendError(data.error), {
              autoClose: 10000,
              toastId: `message-failed-${data.messageId}`,
            });
          }
        }
      } else {
        console.log('⚠️ Ack para ticket diferente:', data.ticketId, 'vs actual:', currentTicketId.current); // DEBUG
      }
    };

    // 🆕 NUEVO: Handler unificado para message:event (arquitectura de rooms)
    const handleMessageEvent = (data) => {
      console.log('📨 message:event recibido:', data);

      if (!data.message || !data.ticket) {
        console.warn('⚠️ Evento de mensaje inválido:', data);
        return;
      }

      const { action, message, ticket, metadata } = data;

      // Validar que el mensaje pertenece al ticket actual
      if (message.ticketId !== currentTicketId.current) {
        console.log('⚠️ Mensaje para ticket diferente:', message.ticketId, 'vs actual:', currentTicketId.current);
        return;
      }

      const messageId = message.id;

      // Deduplicación
      const dedupeKey = `${action}-${messageId}`;
      if (window.processedMessages && window.processedMessages.has(dedupeKey)) {
        console.log('🔄 Mensaje duplicado ignorado:', messageId, 'action:', action);
        return;
      }

      // Marcar como procesado
      if (!window.processedMessages) {
        window.processedMessages = new Set();
      }
      window.processedMessages.add(dedupeKey);
      setTimeout(() => {
        window.processedMessages.delete(dedupeKey);
      }, 2000);

      // Procesar según la acción
      unstable_batchedUpdates(() => {
        if (action === 'create') {
          console.log('✅ Agregando nuevo mensaje:', messageId);

          const quotedMsgId =
            message.quotedMsgId || metadata?.quotedMsgId || null;

          const quotedMsg = resolveQuotedMsg(
            quotedMsgId,
            messagesList,
            message.quotedMsg,
          );

          const enrichedMessage = quotedMsg
            ? { ...message, quotedMsg }
            : message;

          const tempId = message.tempId || metadata?.tempId || null;

          // Fallback heurístico: si el backend aún no envía tempId, intentamos
          // matchear el mensaje entrante contra el último optimista guardado
          // localmente para este ticket (mismo body + fromMe + ventana de tiempo).
          let heuristicTempId = null;
          if (!tempId && message.fromMe && recentOptimisticRef.current.size > 0) {
            const ticketIdNum = Number(ticket.id);
            for (const [key, info] of recentOptimisticRef.current.entries()) {
              if (info.ticketId !== ticketIdNum) continue;
              const msgTime = new Date(message.createdAt).getTime();
              const optTime = new Date(info.createdAt).getTime();
              const within = Math.abs(msgTime - optTime) < 30000; // 30s ventana
              const sameBody = (info.body || "") === (message.body || "");
              if (within && sameBody) {
                heuristicTempId = key;
                break;
              }
            }
          }
          const effectiveTempId = tempId || heuristicTempId;

          // Cache
          if (messagesCache[ticket.id]) {
            setMessagesCache(prev => {
              const ticketCache = prev[ticket.id];
              if (!ticketCache) return prev;

              const matchesTemp = effectiveTempId
                && ticketCache.messages.some(m => m.tempId === effectiveTempId);
              const existsById = ticketCache.messages.some(m => m.id === messageId);

              if (matchesTemp) {
                const updatedMessages = ticketCache.messages
                  .map(m => (m.tempId === effectiveTempId ? enrichedMessage : m))
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                return {
                  ...prev,
                  [ticket.id]: {
                    ...ticketCache,
                    messages: updatedMessages,
                    totalCount: updatedMessages.length
                  }
                };
              }

              if (existsById) return prev;

              const updatedMessages = [...ticketCache.messages, enrichedMessage]
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

              return {
                ...prev,
                [ticket.id]: {
                  ...ticketCache,
                  messages: updatedMessages,
                  totalCount: updatedMessages.length
                }
              };
            });
          }

          // Vista
          if (ticket.id === currentTicketId.current) {
            const matchesTemp = effectiveTempId
              && messagesList.some(m => m.tempId === effectiveTempId);
            const existsById = messagesList.some(msg => msg.id === messageId);

            if (matchesTemp) {
              dispatch({
                type: "RECONCILE_MESSAGE",
                payload: { tempId: effectiveTempId, realMessage: enrichedMessage },
              });
              // Limpiar del registro de recientes
              if (effectiveTempId) recentOptimisticRef.current.delete(effectiveTempId);
            } else if (!existsById) {
              dispatch({
                type: "ADD_MESSAGE",
                payload: enrichedMessage
              });
            }

            if (!message.fromMe) {
              stopContactPresence();
            }

            requestAnimationFrame(() => {
              if (message.fromMe || isNearBottom()) {
                scrollToBottom();
              }
            });
          }
        }

        if (action === 'update') {
          console.log('🔄 Actualizando mensaje:', messageId, 'ack:', message.ack);

          const quotedMsgId =
            message.quotedMsgId || metadata?.quotedMsgId || null;

          const quotedMsg = resolveQuotedMsg(
            quotedMsgId,
            messagesList,
            message.quotedMsg,
          );

          const enrichedMessage = quotedMsg
            ? { ...message, quotedMsg }
            : message;

          // Actualizar cache
          if (messagesCache[ticket.id]) {
            updateTicketMessage(ticket.id, messageId, enrichedMessage);
          }

          // Actualizar vista
          if (ticket.id === currentTicketId.current) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: enrichedMessage
            });

            requestAnimationFrame(() => {
              if (enrichedMessage.fromMe && enrichedMessage.ack >= 1 && isNearBottom()) {
                scrollToBottom();
              }
            });
          }
        }

        if (action === 'delete') {
          console.log('🗑️ Eliminando mensaje:', messageId);

          // Actualizar cache
          if (messagesCache[ticket.id]) {
            updateTicketMessage(ticket.id, messageId, {
              isDeleted: true,
              body: "Este mensaje fue eliminado"
            });
          }

          // Actualizar vista
          if (ticket.id === currentTicketId.current) {
            const updatedMessages = messagesList.map(msg => {
              if (msg.id === messageId) {
                return {
                  ...msg,
                  isDeleted: true,
                  body: "Este mensaje fue eliminado"
                };
              }
              return msg;
            });

            dispatch({ type: "LOAD_MESSAGES", payload: updatedMessages });
          }
        }
      });
    };

    console.log('🔌 Configurando listeners de socket para companyId:', companyId, 'ticketId:', ticketId);

    // 📡 NUEVO: Registrar listener para el evento unificado
    onSocket('message:new', handleMessageEvent, componentId);
    onSocket('message:edited', handleMessageEvent, componentId);
    onSocket('message:ack', handleMessageAck, componentId);
    onSocket('message:revoked', handleMessageDeleted, componentId);

    return () => {
      // Limpiar todos los listeners
      cleanupComponent(componentId);
      console.log('🔌 Listeners de socket limpiados');
    };
  }, [ticketId, messagesList, componentId]); // SOLO ticketId como dependencia

  useEffect(() => {
    if (!ticketId) return undefined;

    const companyId = localStorage.getItem("companyId");
    getSocket(companyId);

    const presenceComponentId = `${componentId}-presence`;

    const isSameOpenTicket = (data) => {
      const incoming = data?.ticketId ?? data?.ticket?.id;
      const openId = ticketIdRef.current ?? currentTicketId.current;
      if (incoming == null || openId == null) return false;
      return String(incoming) === String(openId);
    };

    const handlePresenceUpdate = (payload) => {
      const data = payload && typeof payload === "object" ? payload : null;
      if (!data || !isSameOpenTicket(data)) return;

      const presence = data.presence || {};
      const state = presence.state;

      if (state === "recording") {
        startContactPresence("recording");
        return;
      }

      if (state === "composing" || presence.isTyping === true) {
        startContactPresence("composing");
        return;
      }

      stopContactPresence();
    };

    onSocket("presence:update", handlePresenceUpdate, presenceComponentId);

    return () => {
      cleanupComponent(presenceComponentId);
      stopContactPresence();
    };
  }, [componentId, ticketId, startContactPresence, stopContactPresence]);

  useEffect(() => {
    if (!contactPresence) return;
    const bubble = typingBubbleRef.current;
    if (bubble && typeof bubble.scrollIntoView === "function") {
      bubble.scrollIntoView({ block: "end", behavior: "smooth" });
    }
  }, [contactPresence]);

  // Map<tempId, { body, ticketId, createdAt, kind }> — guarda info del último
  // optimista por ticket para hacer matching heurístico cuando el backend aún
  // no envía tempId en el payload del socket.
  const recentOptimisticRef = useRef(new Map());

  const handleOptimisticMessage = useCallback((optimisticMsg) => {
    if (!optimisticMsg || !optimisticMsg.ticketId) return;
    if (Number(optimisticMsg.ticketId) !== Number(currentTicketId.current)) return;
    dispatch({
      type: "ADD_MESSAGE",
      payload: {
        ...optimisticMsg,
        id: optimisticMsg.tempId || optimisticMsg.id,
      },
    });

    if (optimisticMsg.tempId) {
      recentOptimisticRef.current.set(optimisticMsg.tempId, {
        tempId: optimisticMsg.tempId,
        ticketId: Number(optimisticMsg.ticketId),
        body: optimisticMsg.body || "",
        mediaName: optimisticMsg.mediaName || null,
        mediaType: optimisticMsg.mediaType || null,
        createdAt: optimisticMsg.createdAt,
      });
      // Limpieza: borrar tras 30s para no acumular basura
      setTimeout(() => {
        recentOptimisticRef.current.delete(optimisticMsg.tempId);
      }, 30000);
    }

    requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, []);

  useEffect(() => {
    if (typeof onRegisterOptimisticHandler === "function") {
      onRegisterOptimisticHandler(handleOptimisticMessage);
    }
  }, [onRegisterOptimisticHandler, handleOptimisticMessage]);

  const scrollToBottom = (smooth = false) => {
    const container = messagesContainerRef.current || document.getElementById("messagesList");
    if (!container) return;

    if (smooth) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Scroll inmediato - más confiable para mensajes nuevos
      container.scrollTop = container.scrollHeight;
      
      // Backup para asegurar que el DOM se actualice
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  };

  const isNearBottom = () => {
    const container = messagesContainerRef.current || document.getElementById("messagesList");
    if (!container) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    return (scrollHeight - scrollTop - clientHeight) < 150;
  };

  // 🔄 CARGA DE MENSAJES ANTERIORES OPTIMIZADA PARA INDEXEDDB
  const loadMore = async () => {
    if (!ticketId || loadingMessages[ticketId] || !hasMore || loadingMore) {
      console.log(`⚠️ loadMore bloqueado: ticketId=${!!ticketId}, loading=${loadingMessages[ticketId]}, hasMore=${hasMore}, loadingMore=${loadingMore}`);
      return;
    }
    
    // Validar que tengamos mensajes actuales
    if (!Array.isArray(messagesList) || messagesList.length === 0) {
      console.log("⚠️ Lista de mensajes vacía o inválida");
      return;
    }
    
    try {
      const nextPage = pageNumber + 1;
      console.log(`🔄 loadMore iniciado: página ${nextPage} para ticket ${ticketId}`);
      
      setLoadingMore(true);
      
      // Preservar posición de scroll ANTES de cargar
      const container = messagesContainerRef.current || document.getElementById("messagesList");
      if (!container) {
        console.warn("⚠️ Contenedor de mensajes no encontrado");
        return;
      }
      
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      lastScrollHeight.current = scrollHeight;
      
      console.log(`📍 Preservando scroll: scrollTop=${scrollTop}, scrollHeight=${scrollHeight}`);

      // Usar el contexto IndexedDB mejorado
      const data = await fetchTicketMessages(ticketId, nextPage, false);
      
      if (currentTicketId.current === ticketId && data?.messages) {
        console.log(`✅ IndexedDB devolvió ${data.messages.length} mensajes, hasMore: ${data.hasMore}`);
        
        // El contexto IndexedDB ya maneja la combinación y ordenamiento
        // Solo necesitamos actualizar la vista local
        const currentMessageIds = new Set(messagesList.map(msg => msg.id));
        const newMessages = data.messages.filter(msg => !currentMessageIds.has(msg.id));
        
        if (newMessages.length > 0) {
          console.log(`➕ Agregando ${newMessages.length} mensajes nuevos a la vista`);
          
          // Combinar mensajes existentes + nuevos, manteniendo orden cronológico
          const combinedMessages = [...messagesList, ...newMessages]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .filter((msg, index, self) => index === self.findIndex((m) => m.id === msg.id));

          dispatch({ type: "LOAD_MESSAGES", payload: combinedMessages });
          setPageNumber(nextPage);
          
          console.log(`📊 Vista actualizada: ${combinedMessages.length} mensajes total`);
        } else {
          console.log("ℹ️ No hay mensajes nuevos únicos para mostrar");
        }
        
        // Actualizar estado de hasMore
        setHasMore(data.hasMore || false);
        
      } else {
        console.warn("⚠️ No se recibieron datos válidos del contexto IndexedDB");
        setHasMore(false);
      }

      // 🎯 PRESERVACIÓN MEJORADA DE SCROLL
      // Usar requestAnimationFrame doble para asegurar que el DOM se ha actualizado
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container && container.scrollHeight !== lastScrollHeight.current) {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - lastScrollHeight.current;
            const newScrollTop = scrollTop + heightDifference;
            
            console.log(`📍 Restaurando scroll: ${scrollTop} + ${heightDifference} = ${newScrollTop}`);
            console.log(`📐 Heights - Anterior: ${lastScrollHeight.current}, Nuevo: ${newScrollHeight}`);
            
            container.scrollTop = newScrollTop;
            
            // Verificar que el scroll se aplicó correctamente
            setTimeout(() => {
              const finalScrollTop = container.scrollTop;
              console.log(`✅ Scroll final: ${finalScrollTop} (esperado: ${newScrollTop})`);
            }, 10);
          }
        });
      });

    } catch (err) {
      console.error(`❌ Error en loadMore para ticket ${ticketId}:`, err);
      toastError("Error al cargar más mensajes");
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const updateScrollToLatestButton = (container) => {
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    setShowScrollToLatest(distanceFromBottom > 220);
  };

  // 📜 MANEJO DE SCROLL OPTIMIZADO PARA INDEXEDDB
  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    updateScrollToLatestButton(e.currentTarget);

    if (!hasMore || loadingMessages[ticketId] || loadingMore) {
      return;
    }
    
    // Detectar scroll hacia arriba para cargar mensajes anteriores
    // Umbral optimizado para mejor experiencia con IndexedDB
    const threshold = 150; // Más margen para permitir carga suave
    
    if (scrollTop < threshold) {
      console.log(`📜 Scroll trigger activado: scrollTop=${scrollTop}, threshold=${threshold}, hasMore=${hasMore}`);
      
      // Usar debounce implícito con requestAnimationFrame para evitar llamadas múltiples
      requestAnimationFrame(() => {
        // Verificar nuevamente las condiciones después del frame
        if (!loadingMore && hasMore && ticketId) {
          loadMore();
        }
      });
    }
  };

  const scrollToMessageById = (messageId) => {
    if (!messageId) return false;

    let el = document.getElementById(`msg-${messageId}`);
    if (!el) {
      const match = messagesList.find((m) => String(m.id) === String(messageId));
      if (match) {
        el = document.getElementById(`msg-${match.id}`);
      }
    }
    if (!el) return false;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMessageId(String(messageId));
    window.setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === String(messageId) ? null : current
      );
    }, 1800);
    return true;
  };

  const handleQuotedMessageClick = (event, quotedMsg, fallbackQuotedId) => {
    event.preventDefault();
    event.stopPropagation();

    const quotedId = quotedMsg?.id || fallbackQuotedId;
    if (!quotedId) return;

    if (scrollToMessageById(quotedId)) return;

    pendingScrollToMessageId.current = quotedId;
    pendingScrollAttempts.current = 0;

    if (hasMore && !loadingMore) {
      loadMore();
      return;
    }

    if (!hasMore) {
      pendingScrollToMessageId.current = null;
      toast.info("No se encontró el mensaje citado");
    }
  };

  useEffect(() => {
    const targetId = pendingScrollToMessageId.current;
    if (!targetId || loadingMore) return;

    if (scrollToMessageById(targetId)) {
      pendingScrollToMessageId.current = null;
      pendingScrollAttempts.current = 0;
      return;
    }

    if (hasMore && pendingScrollAttempts.current < 25) {
      pendingScrollAttempts.current += 1;
      loadMore();
      return;
    }

    pendingScrollToMessageId.current = null;
    pendingScrollAttempts.current = 0;
    toast.info("No se encontró el mensaje citado");
  }, [messagesList, hasMore, loadingMore]);

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const handleImageClick = (mediaUrl) => {
    console.log("handleImageClick:", mediaUrl); // Para debug
    console.log("chatImages:", chatImages); // Para debug
    const imageIndex = chatImages.findIndex(url => url === mediaUrl);
    console.log("imageIndex encontrado:", imageIndex); // Para debug
    if (imageIndex !== -1) {
      setSelectedImageIndex(imageIndex);
    }
  };

  const handleCloseModal = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex(prev => 
      prev > 0 ? prev - 1 : chatImages.length - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex(prev => 
      prev < chatImages.length - 1 ? prev + 1 : 0
    );
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImageIndex !== null) {
        if (e.key === 'ArrowLeft') {
          handlePrevImage();
        } else if (e.key === 'ArrowRight') {
          handleNextImage();
        } else if (e.key === 'Escape') {
          handleCloseModal();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImageIndex, chatImages.length]);

  useEffect(() => {
    if (Array.isArray(messagesList) && messagesList.length > 0) {
      const imageUrls = messagesList
        .filter(msg => {
          // Verificar que el mensaje tenga una imagen válida
          return msg.mediaType?.startsWith("image") && 
                 msg.mediaUrl && 
                 msg.message !== "Este mensaje fue eliminado";
        })
        .map(msg => msg.mediaUrl);
      
      // Solo actualizar si hay diferencias en las URLs
      if (JSON.stringify(imageUrls) !== JSON.stringify(chatImages)) {
        console.log("Imágenes encontradas:", imageUrls); // Para debug
        setChatImages(imageUrls);
      }
    }
  }, [messagesList.length, messagesList]); // Usar length como optimización

  const handleDownloadImage = () => {
    if (selectedImageIndex === null || !chatImages[selectedImageIndex]) return;
  
    const imageUrl = chatImages[selectedImageIndex];
    
    // Codificar la URL de Azure correctamente
    const encodedUrl = encodeURIComponent(imageUrl);
		const downloadUrl = `${REACT_APP_BACKEND_URL}/api/public/download?url=${encodedUrl}`;
  
    const link = document.createElement("a");
    link.href = downloadUrl; // Usar la URL del backend
    link.download = 'imagen.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderMessageMedia = (message, showTimestamp = false) => {
    if (!message.mediaUrl) {
      return null;
    }

    const displayName = getMessageFileName(message);
    const mimeType = getMessageMimeType(message);
    const mediaType = (message.mediaType || mimeType || "").toLowerCase();
    const fileType = getFileType(displayName, mimeType);
    const fileSize = formatFileSize(message.fileSize);
    const { color: iconColor, icon: IconComponent } = getFileIconConfig(
      displayName,
      mimeType,
      theme.palette.primary.main
    );

    const renderTimestamp = () => showTimestamp && (
      <span className={classes.timestamp} style={{
        position: 'absolute',
        bottom: '8px',
        right: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: '2px 4px',
        borderRadius: '4px',
        fontSize: '11px'
      }}>
        {renderMessageTimestamp(message)}
        {message.fromMe && renderMessageStatusIcons(message)}
      </span>
    );

    const handleDownload = (event) => {
      event.stopPropagation();

      const link = document.createElement('a');
      link.href = message.mediaUrl;
      link.download = displayName || 'archivo';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const renderDownloadableFile = () => (
      <div className={classes.downloadMedia}>
        <div className={classes.fileContainer}>
          <div
            className={classes.fileIconContainer}
            style={{ backgroundColor: iconColor }}
          >
            <IconComponent className={classes.fileIcon} />
          </div>
          <div className={classes.fileInfo}>
            <div className={classes.fileName} title={displayName || 'Archivo adjunto'}>
              {displayName || 'Archivo adjunto'}
            </div>
            <div className={classes.fileDetails}>
              {fileSize && (
                <span className={classes.fileSize}>
                  {fileSize}
                </span>
              )}
              <span className={classes.fileType}>
                {fileType}
              </span>
            </div>
          </div>
          <button
            className={classes.downloadIconButton}
            onClick={handleDownload}
            title="Descargar archivo"
          >
            <CloudDownloadIcon fontSize="small" />
          </button>
        </div>
        {renderTimestamp()}
      </div>
    );

    if (mediaType.startsWith("image") || mimeType.startsWith("image")) {
      return (
        <div className={classes.messageMediaContainer}>
          <img
            src={message.mediaUrl}
            alt={displayName || "Imagen"}
            className={classes.messageMedia}
            onClick={() => handleImageClick(message.mediaUrl)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      );
    }

    if (mediaType.startsWith("audio") || mimeType.startsWith("audio")) {
      return (
        <CustomAudioPlayer
          audioUrl={message.mediaUrl}
          messageId={message.id}
          showTimestamp={showTimestamp}
          message={message}
          timestampExtra={message.fromMe ? renderMessageStatusIcons(message) : null}
        />
      );
    }

    if (mediaType.startsWith("video") || mimeType.startsWith("video")) {
      return (
        <div className={classes.videoContainer}>
          <video
            src={message.mediaUrl}
            controls
            className={classes.messageMedia}
          />
        </div>
      );
    }

    return renderDownloadableFile();
  };

  const renderMessageTimestamp = (message) =>
    safeFormatDate(getMessageDisplayTimestamp(message), "HH:mm");

  const getFailedSendTooltip = (message) => {
    let errorCode = null;
    if (message?.dataJson) {
      try {
        const parsed =
          typeof message.dataJson === "string"
            ? JSON.parse(message.dataJson)
            : message.dataJson;
        errorCode =
          parsed?.error ??
          (parsed?.metaErrorCode === 131042 ? "payment_method_required" : null) ??
          null;
      } catch (_) {
        errorCode = null;
      }
    }
    return translateBackendError(errorCode) || "Error al enviar mensaje";
  };

  const renderMessageAck = (message) => {
    const scheduleMeta = parseScheduleMessageMeta(message);
    const scheduledPending = isScheduledPendingMessage(message);

    if (message.ack === -1) {
      let paymentRequired = false;
      if (message?.dataJson) {
        try {
          const parsed =
            typeof message.dataJson === "string"
              ? JSON.parse(message.dataJson)
              : message.dataJson;
          paymentRequired = isPaymentMethodRequiredError(
            parsed?.error ?? parsed?.metaErrorCode,
          );
        } catch (_) {
          paymentRequired = false;
        }
      }
      return (
        <HighlightOffIcon
          fontSize="small"
          className={classes.ackIcons}
          style={{
            color: "#f44336",
            cursor: paymentRequired ? "pointer" : "default",
          }}
          title={getFailedSendTooltip(message)}
          onClick={
            paymentRequired
              ? (event) => {
                  event.stopPropagation();
                  openCloudPaymentRequiredDialog();
                }
              : undefined
          }
        />
      );
    }
    if (message.ack === 0) {
      return (
        <AccessTime
          fontSize="small"
          className={classes.ackIcons}
          title={
            scheduledPending
              ? formatScheduleTooltip(scheduleMeta)
              : "Mensaje pendiente"
          }
        />
      );
    }
    if (message.ack === 1) {
      // Enviado al servidor
      return (
        <Done
          fontSize="small"
          className={classes.ackIcons}
          title="Mensaje enviado"
        />
      );
    }
    if (message.ack === 2) {
      // ack=2: el servidor acepto el mensaje (entregue al backend de WhatsApp)
      return (
        <Done
          fontSize="small"
          className={classes.ackIcons}
          title="Mensaje enviado al servidor"
        />
      );
    }
    if (message.ack === 3) {
      // ack=3: le llego al contacto
      return (
        <DoneAll
          fontSize="small"
          className={classes.ackIcons}
          title="Mensaje entregado al contacto"
        />
      );
    }
    if (message.ack === 4) {
      // ack=4: el contacto leyo el mensaje
      return (
        <DoneAll
          fontSize="small"
          className={classes.ackDoneAllIcon}
          title="Mensaje leído por el contacto"
        />
      );
    }

    return null;
  };

  const renderMessageStatusIcons = (message) => (
    <>
      {isTemplateMessage(message) && (
        <DescriptionIcon
          fontSize="small"
          className={classes.messageMetaIcon}
          title="Plantilla"
        />
      )}
      {renderMessageAck(message)}
    </>
  );

  const renderDailyTimestamps = (message, index) => {
    // Verificar que el mensaje y la fecha existan
    if (!message || !messagesList[index]?.createdAt) {
      return null;
    }

    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {safeFormatDate(messagesList[index]?.createdAt, "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      // Verificar que ambas fechas existan antes de usar parseISO
      const currentDate = messagesList[index]?.createdAt;
      const previousDate = messagesList[index - 1]?.createdAt;
      
      if (!currentDate || !previousDate) {
        return null;
      }

      try {
        let messageDay = parseISO(currentDate);
        let previousMessageDay = parseISO(previousDate);

        if (!isSameDay(messageDay, previousMessageDay)) {
          return (
            <span
              className={classes.dailyTimestamp}
              key={`timestamp-${message?.id}`}
            >
              <div className={classes.dailyTimestampText}>
                {safeFormatDate(currentDate, "dd/MM/yyyy")}
              </div>
            </span>
          );
        }
      } catch (error) {
        console.warn("Error al procesar fechas en renderDailyTimestamps:", error);
        return null;
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message?.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index]?.fromMe;
      let previousMessageUser = messagesList[index - 1]?.fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message?.id}`}></span>
        );
      }
    }
  };

  // Función auxiliar para obtener el tipo de mensaje
  const getMessageType = (quotedMsg) => {
    if (!quotedMsg) return '💬 Texto';

    const mediaType = (quotedMsg.mediaType || '').toLowerCase();
    const hasMediaFile = Boolean(quotedMsg.mediaUrl);
    const textLikeTypes = [
      'conversation',
      'extendedtextmessage',
      'extendedtext',
      'chat',
      'text',
      'plaintext',
    ];

    if (mediaType.startsWith('image') || quotedMsg.body?.includes('data:image')) return '📷 Imagen';
    if (mediaType.startsWith('video')) return '🎥 Video';
    if (mediaType.startsWith('audio') || mediaType === 'ptt') return '🎵 Audio';
    if (quotedMsg.body?.includes('BEGIN:VCARD')) return '👤 Contacto';

    if (textLikeTypes.includes(mediaType) || (!hasMediaFile && quotedMsg.body)) {
      if (quotedMsg.body?.match(/^https?:\/\//)) return '🔗 Enlace';
      return '💬 Texto';
    }

    if (hasMediaFile) {
      if (mediaType.includes('pdf')) return '📄 PDF';
      if (mediaType.includes('document') || mediaType.includes('msword')) return '📄 Documento';
      return '📎 Archivo';
    }

    if (quotedMsg.body?.match(/^https?:\/\//)) return '🔗 Enlace';
    return '💬 Texto';
  };

  // Función auxiliar para truncar texto inteligentemente
  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    
    // Buscar el último espacio antes del límite para no cortar palabras
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > maxLength * 0.8) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  };

  // Función auxiliar para formatear timestamp del mensaje citado
  const formatQuotedTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return safeFormatDate(timestamp, "HH:mm");
    } else if (diffInHours < 168) { // 7 días
      return safeFormatDate(timestamp, "ddd HH:mm");
    } else {
      return safeFormatDate(timestamp, "dd/MM HH:mm");
    }
  };

  const renderQuotedMessage = (message) => {
    const quotedMsg = message.quotedMsg;
    if (!quotedMsg) return null;

    const messageType = getMessageType(quotedMsg);
    const displayText = quotedMsg.body || quotedMsg.mediaName || 'Mensaje multimedia';
    const truncatedText = truncateText(displayText, 120);
    const timestamp = formatQuotedTimestamp(quotedMsg.createdAt);
    const senderName = quotedMsg.fromMe ? 'Tú' : (quotedMsg.contact?.name || 'Contacto');

    return (
      <div
        className={clsx(classes.quotedContainerLeft, classes.quotedClickable, {
          [classes.quotedContainerRight]: message?.fromMe,
        })}
        title={`Ir al mensaje: ${displayText}`}
        role="button"
        tabIndex={0}
        onClick={(event) => handleQuotedMessageClick(event, quotedMsg, message.quotedMsgId)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            handleQuotedMessageClick(event, quotedMsg, message.quotedMsgId);
          }
        }}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {/* Header con nombre del remitente y tipo de mensaje */}
          <div className={classes.quotedHeader}>
            <span className={classes.quotedSenderName}>
              {senderName}
            </span>
            <span className={classes.quotedMessageType}>
              {messageType}
            </span>
            {timestamp && (
              <span className={classes.quotedTimestamp}>
                {timestamp}
              </span>
            )}
          </div>
          
          {/* Contenido del mensaje citado */}
          <div className={classes.quotedContent}>
            {/* Mostrar preview de imagen si es una imagen citada */}
            {quotedMsg.mediaUrl && quotedMsg.mediaType && quotedMsg.mediaType.startsWith('image') && (
              <div className={classes.quotedImagePreview}>
                <img 
                  src={quotedMsg.mediaUrl} 
                  alt="Imagen citada"
                  className={classes.quotedImage}
                  onError={(e) => {
                    console.error('Error cargando imagen citada:', quotedMsg.mediaUrl);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            {/* Mostrar texto del mensaje si existe y es relevante */}
            {truncatedText && (
              <div className={classes.quotedText}>
                {truncatedText}
              </div>
            )}
            
            {/* Mostrar nombre del archivo si no hay body pero hay mediaName */}
            {!quotedMsg.body && quotedMsg.mediaName && (
              <div className={classes.quotedFileName}>
                {quotedMsg.mediaName}
              </div>
            )}
            
            {/* Fallback para medios sin body ni mediaName */}
            {!quotedMsg.body && !quotedMsg.mediaName && quotedMsg.mediaUrl && quotedMsg.mediaType && (
              <div className={classes.quotedFileName}>
                {quotedMsg.mediaType.startsWith('image') ? '📷 Imagen' :
                 quotedMsg.mediaType.startsWith('video') ? '🎥 Video' :
                 quotedMsg.mediaType.startsWith('audio') ? '🎵 Audio' : '📎 Archivo'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const messageLocation = (message, createdAt) => {
    return (
      <div className={[classes.textContentItem, { display: 'flex', padding: 5 }]}>
        <img src={message?.split('|')[0]} className={classes.imageLocation} />
        <a
          style={{ fontWeight: '700', color: 'gray' }}
          target="_blank"
          href={message?.split('|')[1]}> Clique para ver localização</a>
        <span className={classes.timestamp}>
          {safeFormatDate(createdAt, "HH:mm")}
        </span>
      </div>
    )
  };
  const renderAvatar = (message) => {
    // Para mensajes enviados por mí, usar la imagen del usuario actual
    if (message.fromMe) {
      return (
        <Avatar
          className={classes.avatarRight}
          src={currentUser?.profile_image || currentUser?.profilePicUrl || currentUser?.profilePic || currentUser?.avatar}
        >
          {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
      );
    }
    
    // Para mensajes recibidos, usar la imagen del contacto
    return (
      <Avatar
        className={classes.avatarLeft}
        src={message?.contact?.profilePicUrl}
      >
        {message?.contact?.name?.charAt(0)?.toUpperCase() || 'C'}
      </Avatar>
    );
  };

  // Modificar la lógica para detectar mensajes de susurro
  // Añadir una función auxiliar para verificar si un mensaje es de tipo susurro
  const isWhisperMessage = (message) => {
    // Log para depuración
    if (message.id && message.id.toString().startsWith('whisper_')) {
      // console.log('Mensaje de susurro detectado por ID:', message);
      // console.log('iswhisper:', message.iswhisper);
      // console.log('isWhisper:', message.isWhisper);
    }
    
    // Verificar todas las posibles variantes del campo iswhisper
    const isWhisper = 
      message.isWhisper === true ||
      (message.id && message.id.toString().startsWith('whisper_'));
    
    // Si es un mensaje de susurro por ID pero no tiene el flag iswhisper,
    // forzamos la propiedad para que se muestre correctamente
    if (message.id && message.id.toString().startsWith('whisper_') && !message.iswhisper) {
      message.iswhisper = true;
    }
    
    return isWhisper;
  };

  // Función auxiliar para detectar si el texto del mensaje es relevante (no solo nombre de archivo)
  const hasRelevantText = (message) => {
    if (!message.body || !message.mediaUrl) {
      return false;
    }

    const body = message.body.trim();

    // Si el body está vacío, no hay texto relevante
    if (!body) {
      return false;
    }

    const fileName = getMessageFileName(message);
    return body.toLowerCase() !== fileName.toLowerCase();
  };

  // ✨ FUNCIONALIDAD DE RESPUESTA RÁPIDA
  // Maneja el click en el área del mensaje para activar respuesta rápida
  // Similar a la funcionalidad de WhatsApp para agilizar las respuestas
  const handleQuickReply = (message, event) => {
    event.stopPropagation();
    console.log('🔄 Respuesta rápida para mensaje:', message.id);
    
    // Usar el contexto de respuesta para establecer el mensaje a responder
    // Esto automáticamente mostrará el mensaje citado en el input de texto
    setReplyingMessage(message);
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        const isLastInSequence = 
          index === messagesList.length - 1 ||
          messagesList[index + 1].fromMe !== message.fromMe;

        const isWhisper = isWhisperMessage(message);
        const additionalStyle = !isLastInSequence
          ? {}
          : (message.fromMe || isWhisper)
            ? { borderBottomRightRadius: 0 }
            : { borderBottomLeftRadius: 0 };

        const whisperClass = isWhisper ? classes.whisperMessage : "";
        
        // Añadir clases según el estado del mensaje
        let messageStateClass = '';
        if (message.fromMe) {
          // ELIMINADO: Ya no aplicamos clase pendingMessage que causaba shimmer
          // Solo aplicamos clase para mensajes fallidos
          if (message.ack === -1) {
            messageStateClass = classes.failedMessage;
          }
          // Los mensajes con ack: 0 ya no tienen efectos visuales especiales
          // Solo muestran el ícono de reloj en renderMessageAck
        }

        if (!message.fromMe && !isWhisper) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}

              <div
                id={`msg-${message.id}`}
                className={clsx(
                  classes.messageLeft,
                  whisperClass,
                  messageStateClass,
                  String(highlightedMessageId) === String(message.id) && classes.highlightedMessage
                )}
                title={message.queueId && message.queue?.name}
                style={additionalStyle}
              >
                {isLastInSequence && !isSmOrLess && renderAvatar(message)}
                
                {/* Área clickeable para respuesta rápida - Mensajes recibidos */}
                {!isWhisper && !quickMessageModalOpen && !emojiModalOpen && (
                  <div
                    id="quickReplyAreaRight"
                    className={classes.quickReplyAreaRight}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!message.isDeleted) {
                        handleQuickReply(message, e);
                      }
                    }}
                    title="Click para responder"
                  />
                )}
                
                {/* Botón visual de respuesta rápida - Mensajes recibidos */}
                {!isWhisper && !quickMessageModalOpen && !emojiModalOpen && (
                  <div
                    id="quickReplyButtonRight"
                    className={classes.quickReplyButtonRight}
                  >
                    <ReplyIcon fontSize="small" />
                  </div>
                )}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                {/* Indicador de mensaje tipo susurro */}
                {isWhisperMessage(message) && (
                  <div className={classes.whisperIndicator}>
                    <VisibilityOffIcon fontSize="small" style={{ marginRight: '4px' }} />
                    <span>Nota privada</span>
                  </div>
                )}

                {message.isDeleted ? (
                  <div className={classes.textContentItem}>
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                    <MarkdownWrapper className={classes.whisperText}>
                      Este mensaje fue eliminado
                    </MarkdownWrapper>
                    <span className={classes.timestamp}>
                      {safeFormatDate(message?.createdAt, "HH:mm")}
                    </span>
                  </div>
                ) : message.mediaUrl ? (
                  // Verificar si hay texto relevante además del archivo multimedia
                  hasRelevantText(message) ? (
                    <div style={{ paddingBottom: '10px' }}>
                      {renderMessageMedia(message, false)}
                      <div className={classes.textContentItem}>
                        {message.quotedMsg && renderQuotedMessage(message)}
                        <MarkdownWrapper className={isWhisperMessage(message) ? classes.whisperText : ''}>
                          {message.body}
                        </MarkdownWrapper>
                        <span className={classes.timestamp}>
                          {safeFormatDate(message?.createdAt, "HH:mm")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    renderMessageMedia(message, true)
                  )
                ) : (
                  message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                    :
                    isVCard(message.body) ?
                      <div className={classes.textContentItem} style={{ padding: '0', marginBottom: '8px' }}>
                        {vCard(message.body)}
                        <span className={classes.timestamp} style={{ right: 10, bottom: 5 }}>
                          {safeFormatDate(message?.createdAt, "HH:mm")}
                        </span>
                      </div>
                    :
                    (<div className={classes.textContentItem}>
                      {message.quotedMsg && renderQuotedMessage(message)}
                      <MarkdownWrapper className={isWhisperMessage(message) ? classes.whisperText : ''}>
                        {message.body}
                      </MarkdownWrapper>
                      <span className={classes.timestamp}>
                        {safeFormatDate(message?.createdAt, "HH:mm")}
                      </span>
                    </div>)
                )}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                id={`msg-${message.id}`}
                className={clsx(
                  classes.messageRight,
                  whisperClass,
                  messageStateClass,
                  String(highlightedMessageId) === String(message.id) && classes.highlightedMessage
                )}
                title={message.queueId && message.queue?.name}
                style={additionalStyle}
              >
                {isLastInSequence && !isSmOrLess && renderAvatar(message)}
                
                {/* Área clickeable para respuesta rápida - Mensajes enviados */}
                {!isWhisper && !quickMessageModalOpen && !emojiModalOpen && (
                  <div
                    id="quickReplyAreaLeft"
                    className={classes.quickReplyAreaLeft}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!message.isDeleted) {
                        handleQuickReply(message, e);
                      }
                    }}
                    title="Click para responder"
                  />
                )}
                
                {/* Botón visual de respuesta rápida - Mensajes enviados */}
                {!isWhisper && !quickMessageModalOpen && !emojiModalOpen && (
                  <div
                    id="quickReplyButtonLeft"
                    className={classes.quickReplyButtonLeft}
                  >
                    <ReplyIcon fontSize="small" />
                  </div>
                )}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {/* Indicador de mensaje tipo susurro */}
                {isWhisperMessage(message) && (
                  <div className={classes.whisperIndicator}>
                    <VisibilityOffIcon fontSize="small" style={{ marginRight: '4px' }} />
                    <span>Nota privada</span>
                  </div>
                )}

                {message.isDeleted ? (
                  <div className={classes.textContentItem}>
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                    <MarkdownWrapper className={classes.whisperText}>
                      Este mensaje fue eliminado
                    </MarkdownWrapper>
                    <span className={classes.timestamp}>
                      {renderMessageTimestamp(message)}
                      {renderMessageStatusIcons(message)}
                    </span>
                  </div>
                ) : message.mediaUrl ? (
                  // Verificar si hay texto relevante además del archivo multimedia
                  hasRelevantText(message) ? (
                    <div style={{ paddingBottom: '10px' }}>
                      {renderMessageMedia(message, false)}
                      <div className={classes.textContentItem}>
                        {message.quotedMsg && renderQuotedMessage(message)}
                        <MarkdownWrapper className={isWhisperMessage(message) ? classes.whisperText : ''}>
                          {message.body}
                        </MarkdownWrapper>
                        <span className={classes.timestamp}>
                          {renderMessageTimestamp(message)}
                          {renderMessageStatusIcons(message)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    renderMessageMedia(message, true)
                  )
                ) : (
                  message.body.includes('data:image') ? messageLocation(message.body, message.createdAt)
                    :
                    isVCard(message.body) ?
                      <div className={classes.textContentItem} style={{ padding: '0', marginBottom: '8px' }}>
                        {vCard(message.body)}
                        <span className={classes.timestamp} style={{ right: 10, bottom: 5 }}>
                          {safeFormatDate(message?.createdAt, "HH:mm")}
                        </span>
                      </div>
                    :
                    (<div className={classes.textContentItem}>
                      {message.quotedMsg && renderQuotedMessage(message)}
                      <MarkdownWrapper className={isWhisperMessage(message) ? classes.whisperText : ''}>
                        {message.body}
                      </MarkdownWrapper>
                      <span className={classes.timestamp}>
                        {renderMessageTimestamp(message)}
                        {renderMessageStatusIcons(message)}
                      </span>
                    </div>)
                )}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return null; // El mensaje vacío se maneja en el JSX principal
    }
  };
  
  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        disableEditDelete={disableCloudMessageEditDelete}
      />
      <div
        id="messagesList"
        ref={messagesContainerRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {/* Indicador de carga inicial */}
        {initialLoading && (
          <div className={classes.initialLoadingContainer}>
            <CircularProgress size={40} />
            <div className={classes.initialLoadingText}>
              Cargando mensajes...
            </div>
          </div>
        )}
        
        {/* Indicador de carga de mensajes anteriores */}
        {loadingMore && (
          <div className={classes.loadingMoreContainer}>
            <CircularProgress size={20} />
            <div className={classes.loadingMoreText}>
              Cargando mensajes anteriores...
            </div>
          </div>
        )}
        
        {/* Lista de mensajes */}
        {!initialLoading && messagesList.length > 0 && renderMessages()}

        {!initialLoading && contactPresence && (
          <div
            ref={typingBubbleRef}
            className={classes.typingBubble}
            aria-label={contactPresence === "recording" ? "Grabando audio" : "Escribiendo"}
          >
            {contactPresence === "recording" ? (
              <MicIcon className={classes.recordingMic} />
            ) : (
              <>
                <span className={classes.typingDot} />
                <span className={classes.typingDot} />
                <span className={classes.typingDot} />
              </>
            )}
          </div>
        )}
        
        {/* Referencia para el final de los mensajes */}
        <div ref={messagesEndRef} />
        
        {/* Mensaje cuando no hay mensajes */}
        {!initialLoading && messagesList.length === 0 && !loadingMessages[ticketId] && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#666',
            fontSize: '16px'
          }}>
            ¡Saluda a tu nuevo contacto!
          </div>
        )}
      </div>
      <IconButton
        aria-label="Ir a los mensajes más recientes"
        title="Ir a los mensajes más recientes"
        className={clsx(
          classes.scrollToLatestButton,
          showScrollToLatest && classes.scrollToLatestButtonVisible
        )}
        onClick={() => {
          setShowScrollToLatest(false);
          scrollToBottom(true);
        }}
      >
        <KeyboardArrowDown />
      </IconButton>
      
      {/* Modal para agregar contacto */}
      {contactModalOpen && (
        <ContactModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          initialValues={contactData}
        />
      )}
      
      <Modal
        open={selectedImageIndex !== null}
        onClose={handleCloseModal}
        className={classes.imageModal}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <div className={classes.imageModalContent}>
          <IconButton 
            className={classes.closeButton}
            onClick={handleCloseModal}
            size="medium"
          >
            <Cancel />
          </IconButton>

          <IconButton 
            className={classes.downloadButton}
            onClick={handleDownloadImage}
            size="medium"
            title="Descargar imagen"
          >
            <CloudDownloadIcon />
          </IconButton>

          {chatImages.length > 1 && (
            <>
              <IconButton
                className={`${classes.navigationButton} ${classes.prevButton}`}
                onClick={handlePrevImage}
                size="medium"
              >
                <BackArrowIcon fill="white" />
              </IconButton>

              <IconButton
                className={`${classes.navigationButton} ${classes.nextButton}`}
                onClick={handleNextImage}
                size="medium"
              >
                <BackArrowIcon fill="white" />
              </IconButton>
            </>
          )}

          {selectedImageIndex !== null && (
            <img
              src={chatImages[selectedImageIndex]}
              alt="Vista ampliada"
              className={classes.modalImage}
              style={{
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MessagesList;
